/**
 * WorkforceRuns — Chris Bauer wave. Port of run-status.jsx: every branch on
 * the latest Generate runs, with per-run drilldown (changes, audit trail,
 * validation). Ops: listGenerateRuns / getRunDetail (REQ-039, kit-served).
 * The table renderer is shared with Priya's Run oversight (RunsTable) — one
 * implementation, two scopes.
 */
import { useState } from "react";
import { Btn, Icon, Pill, T, alpha } from "@v0y4ge/jrni-ui";
import { Screen, PageHeader } from "../../shell/chrome";
import { DataSourceStrip } from "../../shell/opSource";
import { useWfResource } from "./useWorkforce";
import { listGenerateRuns, getRunDetail, type DraftChange } from "./wf-api";
import { RunsTable, RUN_STATUS_META } from "./WorkforceRegionRuns";
import { dayLabel } from "./wf-shared";
import { useT } from "../../lib/i18n";

const KIND_TONE: Record<DraftChange["kind"], "success" | "warn" | "primary" | "info"> = { "New shift": "success", Removed: "warn", "Skill block": "primary", "Time change": "info" };

function RunDrawer({ runId, onClose }: { runId: string; onClose: () => void }) {
  const t = useT();
  const { data, loading, error } = useWfResource(`run:${runId}`, () => getRunDetail(runId));
  return (
    <div role="dialog" aria-label={t("workforce.runs.runDetail", "Run detail")} onClick={onClose} style={{ position: "fixed", inset: 0, background: alpha(T.n1, 35), zIndex: 200, display: "flex", justifyContent: "flex-end" }}>
      <div onClick={(e) => e.stopPropagation()} style={{ width: 640, maxWidth: "100vw", height: "100vh", overflowY: "auto", background: T.white, boxShadow: T.shadowLg }}>
        {error ? <div style={{ padding: 40, textAlign: "center", color: T.soft }}>{t("workforce.runs.couldnTLoadTheRun", "Couldn't load the run:")}{" "}{error}</div>
          : loading || !data ? <div style={{ padding: 40, textAlign: "center", color: T.soft }}>{t("common.status.loading", "Loading…")}</div>
          : <>
            <div style={{ padding: "20px 24px", borderBottom: `1px solid ${T.n5}`, background: T.n6 }}>
              <Btn size="sm" variant="secondary" onClick={onClose}><Icon.x size={12} /> {t("common.actions.close", "Close")}</Btn>
              <div style={{ display: "flex", gap: 8, alignItems: "center", margin: "14px 0 4px" }}>
                <span style={{ fontSize: 11, color: T.soft, fontFamily: T.fontMono }}>{data.runId}</span>
                <Pill tone={RUN_STATUS_META[data.status].tone} dot>{t(RUN_STATUS_META[data.status].labelKey, RUN_STATUS_META[data.status].label)}</Pill>
              </div>
              <div style={{ fontSize: 20, fontWeight: 700, color: T.n1 }}>{data.branchName}</div>
              <div style={{ fontSize: 12.5, color: T.n2, marginTop: 2 }}>{t("workforce.runs.weekOf", "Week of")}{" "}{dayLabel(data.week, "full")} {t("workforce.runs.generatedBy", "· generated by")}{" "}{data.generatedBy}</div>
            </div>
            <div style={{ padding: "16px 24px", display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 14, borderBottom: `1px solid ${T.n6}` }}>
              {[["Coverage", `${data.coveragePct}%`, data.coveragePct >= 95 ? T.green1 : T.n1],
                ["Cost Δ", `${data.costDelta > 0 ? "+" : ""}${data.costDelta.toFixed(1)}%`, data.costDelta > 0 ? T.orange1 : T.green1],
                ["Errors", String(data.validation.errors), data.validation.errors > 0 ? T.red1 : T.green1],
                ["Warnings", String(data.validation.warnings), data.validation.warnings > 0 ? T.orange1 : T.green1]].map(([l, v, c]) => (
                <span key={l as string} style={{ display: "flex", flexDirection: "column", gap: 2 }}><span style={{ fontSize: 11, color: T.soft }}>{l}</span><span style={{ fontSize: 17, fontWeight: 700, fontFamily: T.fontMono, color: c as string }}>{v}</span></span>
              ))}
            </div>
            <div style={{ padding: "16px 24px", borderBottom: `1px solid ${T.n6}` }}>
              <div style={{ fontSize: 11, color: T.soft, textTransform: "uppercase", letterSpacing: 0.05, fontWeight: 700, marginBottom: 10 }}>{t("workforce.runs.changes", "Changes ·")}{data.changes.length}</div>
              {data.changes.map((c) => (
                <div key={c.id} style={{ display: "flex", gap: 10, alignItems: "center", padding: "9px 0", borderBottom: `1px solid ${T.n6}`, flexWrap: "wrap", fontSize: 12.5 }}>
                  <span style={{ width: 120, flexShrink: 0, display: "flex", flexDirection: "column" }}><b style={{ color: T.n1, fontSize: 12.5 }}>{c.advisor}</b><span style={{ fontSize: 10.5, color: T.soft, fontFamily: T.fontMono }}>{c.day}</span></span>
                  <Pill tone={KIND_TONE[c.kind]}>{c.kind}</Pill>
                  <span style={{ display: "flex", gap: 6, alignItems: "center", flex: 1, minWidth: 170, fontFamily: T.fontMono, fontSize: 11.5 }}>
                    <span style={{ color: T.soft, textDecoration: "line-through" }}>{c.from}</span><span style={{ color: T.soft }}>→</span><b style={{ color: T.n1 }}>{c.to}</b>
                  </span>
                </div>
              ))}
            </div>
            <div style={{ padding: "16px 24px" }}>
              <div style={{ fontSize: 11, color: T.soft, textTransform: "uppercase", letterSpacing: 0.05, fontWeight: 700, marginBottom: 10 }}>{t("workforce.runs.auditTrail", "Audit trail")}</div>
              {data.audit.map((a, i) => (
                <div key={i} style={{ display: "flex", gap: 12, padding: "8px 0", borderBottom: `1px solid ${T.n6}`, fontSize: 12.5, alignItems: "baseline" }}>
                  <span style={{ fontFamily: T.fontMono, fontSize: 11, color: T.soft, width: 150, flexShrink: 0 }}>{a.ts.slice(0, 16).replace("T", " ")}</span>
                  <b style={{ color: T.n1, width: 150, flexShrink: 0 }}>{a.actor}</b>
                  <span style={{ color: T.n2 }}>{a.action}</span>
                </div>
              ))}
            </div>
          </>}
      </div>
    </div>
  );
}

export default function WorkforceRuns() {
  const t = useT();
  const { data, loading, error } = useWfResource("wfruns", () => listGenerateRuns());
  const [pick, setPick] = useState<string | null>(null);
  const runs = data?.runs ?? [];
  const counts = {
    published: runs.filter((r) => r.status === "published").length,
    review: runs.filter((r) => r.status === "in-review").length,
    escalated: runs.filter((r) => r.status === "escalated").length,
    failed: runs.filter((r) => r.status === "failed").length,
  };
  return (
    <Screen rail="wf-runs">
      <PageHeader title={t("workforce.runs.runStatus", "Run status")} subtitle={t("workforce.runs.whereEveryBranchStandsOnTheLatestGenerateRunPublishedInReview", "Where every branch stands on the latest Generate run — published, in review, escalated — plus the audit trail.")} breadcrumb={null} />
      <DataSourceStrip screen="wfruns" ops={["listGenerateRuns", "getRunDetail"]} reqId="REQ-039" />
      <div style={{ flex: 1, overflow: "auto", padding: "14px 24px 32px", display: "flex", flexDirection: "column", gap: 14 }}>
        {error ? <div style={{ padding: 40, textAlign: "center", color: T.soft }}>{t("workforce.runs.couldnTLoadRuns", "Couldn't load runs:")}{" "}{error}</div>
          : loading || !data ? <div style={{ padding: 40, textAlign: "center", color: T.soft, fontSize: 14 }}>{t("common.status.loading", "Loading…")}</div>
          : <>
            <div style={{ display: "flex", gap: 18, padding: "12px 18px", background: T.white, border: `1px solid ${T.border}`, borderRadius: 10, flexWrap: "wrap" }}>
              {[["Published", counts.published, T.green1], ["In review", counts.review, T.p1], ["Escalated", counts.escalated, T.orange1], ["Failed", counts.failed, counts.failed > 0 ? T.red1 : T.soft]].map(([l, v, c]) => (
                <span key={l as string} style={{ display: "flex", flexDirection: "column", gap: 1 }}><span style={{ fontSize: 11, color: T.soft }}>{l}</span><span style={{ fontSize: 18, fontWeight: 700, fontFamily: T.fontMono, color: c as string }}>{v as number}</span></span>
              ))}
              <span style={{ marginLeft: "auto", alignSelf: "center", fontSize: 12, color: T.soft }}>{t("workforce.runs.clickARunForItsChangesValidationAndAuditTrail", "Click a run for its changes, validation and audit trail.")}</span>
            </div>
            <RunsTable runs={runs} testid="wfruns-row" onPick={setPick} />
          </>}
      </div>
      {pick ? <RunDrawer runId={pick} onClose={() => setPick(null)} /> : null}
    </Screen>
  );
}
